export enum ERiskBand {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export enum ERiskAlertSeverity {
  CRITICAL = 'critical',
  WARNING = 'warning',
  INFO = 'info',
}

export enum ERiskAlertType {
  PAYMENT_DELAY = 'paymentDelay',
  INACTIVE_CLIENT = 'inactiveClient',
  SCORE_DROP = 'scoreDrop',
  GROWING_DEFAULT = 'growingDefault',
  LIMIT_NEAR_MAX = 'limitNearMax',
  ANTICIPATION_OPPORTUNITY = 'anticipationOpportunity',
  OUTDATED_QUERIES = 'outdatedQueries',
}

export enum EFinBankRiskAnalysisViews {
  NOT_HIRED = 0,
  DASHBOARD = 1,
}

export enum EEvolutionWindow {
  DAYS_30 = '30d',
  DAYS_15 = '15d',
  DAYS_7 = '7d',
}

export enum EDistributionBand {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  UNSCORED = 'unscored',
  NOT_QUERIED = 'notQueried',
}

export interface IPeriodFilter {
  startDate?: Date;
  endDate?: Date;
}

export interface IRiskAnalysisFilter {
  period: IPeriodFilter;
}

/**
 * Filtros do host `sankhya-finance` (tela Análise de Risco): propriedade `filters` + evento
 * `sankhyafinance:filterschange`. Único filtro da tela — o período por datas. Ver
 * docs/riskanalysisfiltros no host.
 */
export interface IRiskAnalysisHostFilters {
  /** Data inicial do período (ISO `yyyy-MM-dd`), ou null quando não há intervalo. */
  dataInicio: string | null;
  /** Data final do período (ISO `yyyy-MM-dd`), ou null quando não há intervalo. Nunca no futuro (normalizado no host). */
  dataFim: string | null;
}

export type TKpiVariationUnit = 'percent' | 'percentagePoints' | 'points' | 'count';

export interface IKpiVariation {
  value: number;
  unit: TKpiVariationUnit | string;
}

export interface IKpiIndicator {
  formattedValue: string;
  detailedValue?: string;
  variation?: IKpiVariation;
}

export interface IRiskAnalysisKpis {
  totalExposure: IKpiIndicator;
  highRiskShare: IKpiIndicator;
  criticalPartners: IKpiIndicator;
  averageScore: IKpiIndicator;
}

export interface IScoreSummary {
  averageScore: number;
}

export interface IRiskDistributionRow {
  band: EDistributionBand;
  companyPercent: number | null;
  benchmarkPercent: number;
}

export interface IEvolutionPoint {
  label: string;
  averageScore: number | null;
  highRiskAverageScore: number | null;
  exposure: number | null;
}

export interface IPartnerScore {
  id: string;
  name?: string;
  partnerCode?: string;
  score: number;
  scoreVariationPoints: number;
  scoreVariationPercent: number;
  usedLimit: number;
  formattedUsedLimit: string;
}

export interface IRiskAlert {
  type: ERiskAlertType;
  severity: ERiskAlertSeverity;
  affectedCount: number;
}

export interface IPageRequest {
  offset: number;
  limit: number;
}

export interface IRiskAnalysisRequestOptions {
  refresh?: boolean;
}

export interface IContractCheckoutData {
  codParc?: string;
  currentPlanId?: string;
  purchaseDate?: string;
  externalContractId?: string;
}

export interface IPagedResult<T> {
  items: Array<T>;
  total: number;
  offset: number;
}

export interface IPartnersScoreModalContext {
  alertType?: ERiskAlertType;
}

export interface IPartnerScoreDetails {
  creditScoreData: import('../../fin-bank-credit-score/subcomponents/score-dashboard/subcomponents/score-result/types').ICreditScoreData;
  clientInfoData: import('../../fin-bank-credit-score/subcomponents/score-dashboard/subcomponents/score-info/types').IClientInfoData;
}
